import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridApiPro,
  GridFilterModel,
  GridInputRowSelectionModel,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FilterTypes, OrderDirection, PageInfo, PaginationInput } from '@typings/graphql'
import { ApolloQueryResult } from '@apollo/client'
import { debounce } from '@mui/material'

import { useFilterParameter } from './hooks/useFilterParameter'
import { useDataGridTranslation } from './hooks/useDataGridTranslation'
import { DATA_GRID_PAGE_SIZE, DATA_GRID_PAGE_SIZE_OPTIONS, DEFAULT_PAGINATION } from './constants/dataGrid'
import { BaseColumnType } from './types/types'
import { DataGridToolbar } from './components/DataGridToolbar'
import { getMappedFilterOperator } from './utils/filter'

export type BaseDataGridHandler = {
  gridApiRef?: GridApiPro,
  refetchVariables: PaginationInput,
  refetch: () => Promise<void>
}

export type BaseDataGridProps<T extends GridValidRowModel> = {
  checkable?: boolean
  loading?: boolean
  columns: BaseColumnType<T>[]
  rows: T[]
  gridKey: string
  serverSide?: boolean,
  totalRows?: number
  pageInfo?: PageInfo,
  defaultOrderBy?: GridSortModel
  toolbarExtension?: React.ReactElement
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void
  refetch?: (variables: PaginationInput) => Promise<ApolloQueryResult<any>>
}

const BaseDataGrid = function <T extends GridValidRowModel> ({
  rows,
  columns,
  loading,
  gridKey,
  serverSide = false,
  checkable = false,
  totalRows,
  pageInfo,
  toolbarExtension,
  defaultOrderBy,
  onRowSelectionModelChange,
  refetch
}: BaseDataGridProps<T>,
ref: React.ForwardedRef<BaseDataGridHandler>
): React.ReactElement {
  const { t } = useTranslation()

  const { parseToFilter } = useFilterParameter()
  const [internalLoading, setInternalLoading] = React.useState(false)

  const [rowSelectionModel, setRowSelectionModel] = React.useState<GridInputRowSelectionModel>()

  const refetchVariablesRef = React.useRef<PaginationInput>({
    offset: 0,
    limit: DATA_GRID_PAGE_SIZE,
    ...(defaultOrderBy && {
      orderBy: {
        field: defaultOrderBy[0]?.field || '',
        direction: defaultOrderBy[0]?.sort as OrderDirection
      }
    })
  })
  const rowCountRef = React.useRef(totalRows || 0)

  const apiRef = useGridApiRef() as React.MutableRefObject<GridApiPro>

  const rowCount = React.useMemo(() => {
    if (totalRows !== undefined) {
      rowCountRef.current = totalRows
    }
    return rowCountRef.current
  }, [totalRows])

  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>(DEFAULT_PAGINATION)
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>()
  const [sortModel, setSortModel] = React.useState<GridSortModel | undefined>(defaultOrderBy)

  const dataGridTranslation = useDataGridTranslation()

  React.useEffect(() => {
    setPaginationModel((prev) => ({
      page: pageInfo?.currentPage || prev.page,
      pageSize: pageInfo?.perPage || prev.pageSize
    }))
  }, [pageInfo])

  const mappedColumns = React.useMemo(() => {
    const gridFormattedKey = gridKey.split(':')[0]

    const mapped = [
      ...columns.map((column) => {
        return {
          disableColumnMenu: true,
          ...(!column.headerName && { headerName: column.hideHeader ? '' : t(`${gridFormattedKey}.column.${column.field}`) }),
          ...column
        }
      })]

    return mapped
  }, [columns])

  const handleRefetch = async (refetchVariables?: PaginationInput) => {
    if (!serverSide) {
      return
    }

    setInternalLoading(true)

    await refetch?.(
      refetchVariables || refetchVariablesRef.current
    )

    apiRef.current?.setRowSelectionModel([])

    setInternalLoading(false)
  }

  React.useImperativeHandle(ref, () => ({
    gridApiRef: apiRef.current as GridApiPro,
    refetchVariables: refetchVariablesRef.current,
    refetch: handleRefetch
  }))

  const handlePaginationChange = (model: GridPaginationModel) => {
    const pageSizeChanged = paginationModel.pageSize !== model.pageSize

    refetchVariablesRef.current = {
      ...refetchVariablesRef.current,
      limit: model.pageSize,
      offset: pageSizeChanged ? 0 : model.page * model.pageSize
    }

    setPaginationModel(model)

    handleRefetch()
  }

  const getMappedGridFilter = (model: GridFilterModel) => {
    return model.items.filter((item) => !!item?.field && item.value !== undefined && item.value !== null && item.value !== '').map((item) => {
      const type = columns.find((column) => column.field === item.field)?.filterType || FilterTypes.String

      return ({
        field: item.field,
        operator: getMappedFilterOperator(item.operator, type),
        value: item.value,
        type
      })
    }).filter((item) => item.value !== undefined && item.value !== null)
  }

  const handleFilterModelChange = React.useCallback(debounce((model: GridFilterModel) => {
    setPaginationModel((prev) => ({
      ...prev,
      page: 0
    }))

    refetchVariablesRef.current = {
      ...refetchVariablesRef.current,
      filter: getMappedGridFilter(model),
      offset: 0
    }

    setFilterModel(model)

    handleRefetch()
  }, 300), [refetch])

  const getMappedGridOrder = (model: GridSortModel) => {
    return model[0]
      ? {
          field: model[0]?.field || '',
          direction: model[0]?.sort as OrderDirection
        }
      : null
  }

  const handleSortModelChange = (model: GridSortModel) => {
    refetchVariablesRef.current = {
      ...refetchVariablesRef.current,
      orderBy: getMappedGridOrder(model),
      offset: 0
    }

    setPaginationModel((prev) => ({
      ...prev,
      page: 0
    }))

    setSortModel(model)

    handleRefetch()
  }

  const handleRowSelectionModelChange = (model: GridRowSelectionModel) => {
    setRowSelectionModel(model)

    onRowSelectionModelChange?.(model)
  }

  React.useEffect(() => {
    const filter = parseToFilter()

    if (filter) {
      apiRef.current?.setFilterModel(filter)
    }
  }, [])

  const handleResetFilter = () => {
    apiRef.current?.setFilterModel({
      items: []
    })
  }

  return (
    <DataGridPro
      apiRef={apiRef}
      loading={loading || internalLoading}
      columns={mappedColumns}
      rows={rows}
      rowCount={rowCount}
      pagination
      disableColumnSelector
      disableRowSelectionOnClick
      {...(checkable && {
        rowSelection: true,
        rowSelectionModel,
        onRowSelectionModelChange: handleRowSelectionModelChange,
        checkboxSelection: true
      })}
      pinnedColumns={{
        left: [...(checkable ? [GRID_CHECKBOX_SELECTION_COL_DEF.field] : [])],
        right: ['action']
      }}
      localeText={{
        ...dataGridTranslation,
        filterPanelOperator: t('common.options')
      }}
      slots={{
        toolbar: () => <DataGridToolbar
          toolbarExtension={toolbarExtension}
          onResetFilter={handleResetFilter}
        />
      }}
      pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
      paginationMode={serverSide ? 'server' : 'client'}
      paginationModel={paginationModel}
      onPaginationModelChange={(model) => handlePaginationChange(model)}
      sortingMode={serverSide ? 'server' : 'client'}
      sortModel={sortModel}
      onSortModelChange={(model) => handleSortModelChange(model)}
      filterMode={serverSide ? 'server' : 'client'}
      filterModel={filterModel}
      onFilterModelChange={(model) => handleFilterModelChange(model)}
    />
  )
}

export default React.forwardRef(BaseDataGrid) as <T extends GridValidRowModel>(
  props: BaseDataGridProps<T> & { ref?: React.ForwardedRef<BaseDataGridHandler> }
) => React.ReactElement
